// ReparadorSTL.jsx
import React, { useState, useRef, useEffect } from "react";
import * as THREE from "three";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils";

import { initThreeScene } from "./pasosreparar/threeSetup.js";
import { loadSTL, exportSTL } from "./pasosreparar/stlUtils.js";
import { geomToTriangles, computeBoundingBox } from "./pasosreparar/geometryUtils.js";
import { buildSDFGrid } from "./pasosreparar/sdfBuilder.js";

/**
 * Componente principal: configura UI, escena y pipeline SDF -> Marching Cubes -> STL
 */
export default function ReparadorSTL() {
  const mountRef = useRef(null);

  // escena (se colocan después de init)
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const meshRef = useRef(null);

  // estado UI
  const [status, setStatus] = useState("Esperando archivo...");
  const [progress, setProgress] = useState("");
  const [voxelResolution, setVoxelResolution] = useState(96);
  const [voxelPadding, setVoxelPadding] = useState(1.05);
  const [loadedGeom, setLoadedGeom] = useState(null);
  const [processedBlob, setProcessedBlob] = useState(null);

  // init three scene
  useEffect(() => {
    const setup = initThreeScene(mountRef, { height: 480, enableGrid: true, enableAxes: false });
    if (!setup) return;
    sceneRef.current = setup.scene;
    cameraRef.current = setup.camera;
    rendererRef.current = setup.renderer;
    controlsRef.current = setup.controls;

    // cleanup on unmount
    return () => {
      try {
        setup.cleanup?.();
      } catch (e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // solo una vez

  // show geometry helper: removes previous mesh, centers and adds to scene
  function showGeometry(geom, color = 0xffcc00) {
    if (!sceneRef.current) return;
    // remove old
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      try { meshRef.current.geometry.dispose(); } catch (e) {}
      meshRef.current = null;
    }
    const mat = new THREE.MeshStandardMaterial({ color, metalness: 0.15, roughness: 0.7, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    // center mesh in scene (keep world coords)
    geom.computeBoundingBox();
    if (geom.boundingBox) {
      const box = geom.boundingBox;
      const center = new THREE.Vector3();
      box.getCenter(center);
      mesh.position.sub(center);
    }

    sceneRef.current.add(mesh);
    meshRef.current = mesh;
  }

  // file load using modular loadSTL
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus("📥 Cargando STL...");
    setProgress("");
    setProcessedBlob(null);

    try {
      const geom = await loadSTL(file); // espera BufferGeometry
      if (!geom || !geom.attributes || !geom.attributes.position) {
        throw new Error("El STL no contiene geometría válida.");
      }
      // clone non-indexed for safety
      const safeGeom = geom.index ? geom.toNonIndexed() : geom.clone();
      setLoadedGeom(safeGeom);
      showGeometry(safeGeom, 0xffcc00);
      setStatus("✅ STL cargado. Listo para reparar.");
    } catch (err) {
      console.error("loadSTL error:", err);
      setStatus("❌ Error cargando STL: " + (err.message || err));
    }
  }

  // remove degenerate triangles helper (keeps BufferGeometry)
  function removeDegenerateTriangles(geom, eps = 1e-12) {
    geom = geom.index ? geom.toNonIndexed() : geom.clone();
    const pos = geom.attributes.position.array;
    const out = [];
    for (let i = 0; i < pos.length; i += 9) {
      const ax = pos[i], ay = pos[i+1], az = pos[i+2];
      const bx = pos[i+3], by = pos[i+4], bz = pos[i+5];
      const cx = pos[i+6], cy = pos[i+7], cz = pos[i+8];
      const abx = bx - ax, aby = by - ay, abz = bz - az;
      const acx = cx - ax, acy = cy - ay, acz = cz - az;
      const crossx = aby * acz - abz * acy;
      const crossy = abz * acx - abx * acz;
      const crossz = abx * acy - aby * acx;
      const area2 = crossx*crossx + crossy*crossy + crossz*crossz;
      if (area2 > eps) out.push(ax,ay,az,bx,by,bz,cx,cy,cz);
    }
    const g = new THREE.BufferGeometry();
    if (out.length === 0) return g;
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(out), 3));
    return g;
  }

  // marching cubes fallback (robusto) - triangula por cubo detectando cambios de signo en los 12 bordes
  // Retorna BufferGeometry o null si no hay triángulos
  function marchingCubesFromSDF(sdf, reportProgress = (s) => {}) {
    const { grid, nx, ny, nz, dx, dy, dz, min } = sdf;
    if (!grid) return null;

    const vertexInterp = (p1, p2, v1, v2) => {
      if (Math.abs(v1) < 1e-12) return p1.slice();
      if (Math.abs(v2) < 1e-12) return p2.slice();
      if (Math.abs(v1 - v2) < 1e-12) return p1.slice();
      const t = v1 / (v1 - v2);
      return [
        p1[0] + (p2[0] - p1[0]) * t,
        p1[1] + (p2[1] - p1[1]) * t,
        p1[2] + (p2[2] - p1[2]) * t,
      ];
    };

    const getVal = (ix, iy, iz) => {
      if (ix < 0 || ix >= nx || iy < 0 || iy >= ny || iz < 0 || iz >= nz) return 1e6;
      return grid[ix + iy * nx + iz * nx * ny];
    };

    const vertices = [];
    const faces = [];

    const edges = [[0,1],[1,3],[3,2],[2,0],[4,5],[5,7],[7,6],[6,4],[0,4],[1,5],[3,7],[2,6]];

    let cnt = 0;
    const total = (nx-1)*(ny-1)*(nz-1);
    const reportEvery = Math.max(1, Math.floor(total / 20));

    for (let iz = 0; iz < nz - 1; iz++) {
      for (let iy = 0; iy < ny - 1; iy++) {
        for (let ix = 0; ix < nx - 1; ix++) {
          // corners
          const p = [];
          const val = [];
          for (let k = 0; k < 8; k++) {
            const cx = ix + (k & 1);
            const cy = iy + ((k >> 1) & 1);
            const cz = iz + ((k >> 2) & 1);
            p.push([ min[0] + cx*dx, min[1] + cy*dy, min[2] + cz*dz ]);
            val.push(getVal(cx, cy, cz));
          }
          // edges sign change
          const edgeVerts = {};
          let signChanges = 0;
          for (let e = 0; e < 12; e++) {
            const a = edges[e][0], b = edges[e][1];
            if ((val[a] < 0 && val[b] >= 0) || (val[a] >= 0 && val[b] < 0)) {
              const v = vertexInterp(p[a], p[b], val[a], val[b]);
              const vid = vertices.length;
              vertices.push(v);
              edgeVerts[e] = vid;
              signChanges++;
            }
          }
          if (signChanges >= 3) {
            const ids = Object.values(edgeVerts);
            if (ids.length >= 3) {
              for (let t = 1; t < ids.length - 1; t++) {
                faces.push([ ids[0], ids[t], ids[t+1] ]);
              }
            }
          }

          cnt++;
          if ((cnt % reportEvery) === 0) {
            const pct = Math.round((cnt / total) * 100);
            reportProgress(`Marching Cubes: ${pct}%`);
          }
        }
      }
    }

    if (vertices.length === 0 || faces.length === 0) return null;

    // build flat positions
    const flat = new Float32Array(faces.length * 9);
    let pidx = 0;
    for (let fi = 0; fi < faces.length; fi++) {
      const f = faces[fi];
      const a = vertices[f[0]], b = vertices[f[1]], c = vertices[f[2]];
      flat[pidx++] = a[0]; flat[pidx++] = a[1]; flat[pidx++] = a[2];
      flat[pidx++] = b[0]; flat[pidx++] = b[1]; flat[pidx++] = b[2];
      flat[pidx++] = c[0]; flat[pidx++] = c[1]; flat[pidx++] = c[2];
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(flat, 3));
    geom.computeVertexNormals();
    return geom;
  }

  // Main repair pipeline
  async function handleRepair() {
    if (!loadedGeom) { setStatus("Sube primero un STL"); return; }
    setStatus("🔧 Iniciando reparación volumétrica...");
    setProgress("Preparando triángulos...");

    try {
      // 1) triangles
      const tris = geomToTriangles(loadedGeom);
      if (!tris || tris.length === 0) {
        setStatus("❌ No hay triángulos en la geometría.");
        return;
      }
      setProgress(`Triángulos: ${tris.length}`);

      // 2) bbox + padded
      const bbox = computeBoundingBox(tris);
      const center = [(bbox.min[0]+bbox.max[0])/2, (bbox.min[1]+bbox.max[1])/2, (bbox.min[2]+bbox.max[2])/2];
      const halfX = (bbox.max[0] - bbox.min[0]) * 0.5 * voxelPadding;
      const halfY = (bbox.max[1] - bbox.min[1]) * 0.5 * voxelPadding;
      const halfZ = (bbox.max[2] - bbox.min[2]) * 0.5 * voxelPadding;
      const padded = {
        min: [center[0] - halfX, center[1] - halfY, center[2] - halfZ],
        max: [center[0] + halfX, center[1] + halfY, center[2] + halfZ],
      };

      // compute grid dims preserving aspect ratio
      const sizeX = padded.max[0] - padded.min[0];
      const sizeY = padded.max[1] - padded.min[1];
      const sizeZ = padded.max[2] - padded.min[2];
      const maxDim = Math.max(sizeX, sizeY, sizeZ);
      const nx = Math.max(2, voxelResolution);
      const ny = Math.max(2, Math.round(voxelResolution * (sizeY / maxDim)));
      const nz = Math.max(2, Math.round(voxelResolution * (sizeZ / maxDim)));

      setProgress(`Voxelización (${nx}×${ny}×${nz})...`);
      // 3) build SDF
      const sdf = buildSDFGrid(tris, padded, nx, ny, nz, setProgress);

      // 4) marching cubes
      setProgress("Aplicando Marching Cubes...");
      const mcGeom = marchingCubesFromSDF(sdf, setProgress);
      if (!mcGeom) {
        setStatus("⚠️ Marching Cubes no generó geometría (aumenta resolución).");
        setProgress("");
        return;
      }

      setProgress("Post-procesando geometría...");
      // 5) cleanup degenerate triangles
      let outGeom = removeDegenerateTriangles(mcGeom);
      // merge vertices
      try {
        outGeom = BufferGeometryUtils.mergeVertices(outGeom, 1e-6);
      } catch (err) {
        console.warn("mergeVertices fallo:", err);
      }
      outGeom.computeVertexNormals();
      outGeom.computeBoundingBox();
      outGeom.computeBoundingSphere();

      setProgress("Exportando STL...");
      // 6) export using modular exportSTL
      const blob = exportSTL(outGeom);
      setProcessedBlob(blob);

      setStatus("✅ Reparación completa — STL listo para descargar");
      setProgress("");
      showGeometry(outGeom, 0x0066ff);
    } catch (err) {
      console.error("Pipeline error:", err);
      setStatus("❌ Error en reparación: " + (err.message || err));
      setProgress("");
    }
  }

  function descargarSTL() {
    if (!processedBlob) { alert("Primero procesa un STL"); return; }
    const url = URL.createObjectURL(processedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "reparado.stl";
    a.click();
  }

  return (
    <div style={{ padding: 16, fontFamily: "sans-serif", background: "white" }}>
      <h2>🛠 3dfyrion Reparador STL — Volumétrico (SDF + Marching Cubes)</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
        <input type="file" accept=".stl" onChange={handleFile} />
        <label>Resolución voxels:</label>
        <input type="number" value={voxelResolution} min={16} max={256}
               onChange={(e) => setVoxelResolution(Math.max(2, Number(e.target.value || 0)))}
               style={{ width: 80 }} />
        <label>Padding bbox:</label>
        <input type="number" step="0.01" value={voxelPadding}
               onChange={(e) => setVoxelPadding(Number(e.target.value || 1.0))}
               style={{ width: 80 }} />
        <button onClick={handleRepair} style={{ padding: "8px 14px", background:"#007bff", color:"white", border:"none", borderRadius:6 }}>
          Reparar (SDF → Marching)
        </button>
        <button onClick={descargarSTL} disabled={!processedBlob}
                style={{ padding: "8px 14px", marginLeft: 8, background: processedBlob ? "green" : "#999", color: "white", border: "none", borderRadius: 6 }}>
          Descargar STL Reparado
        </button>
      </div>

      <p style={{ fontWeight: "bold" }}>{status}</p>
      <p style={{ color: "#666" }}>{progress}</p>

      <div ref={mountRef} style={{ width: "100%", height: 480, border: "1px solid #ccc", marginTop: 12 }} />

      {processedBlob && (
        <p style={{ marginTop: 8 }}>
          STL listo — <a href={URL.createObjectURL(processedBlob)} download="reparado.stl">Descargar</a>
        </p>
      )}

      <p style={{ color: "#555", marginTop: 8 }}>
        Debug: revisa la consola para ver logs. Si quieres más fidelidad en Marching Cubes, puedo integrar la tabla triTable completa y optimizar la triangulación.
      </p>
    </div>
  );
}
